import React, { useEffect, useState } from "react";
import MDButton from "components/MDButton";
import MDModal from "components/MDModal";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAppServices, uploadImageToGhl } from "hook/services";
import {
  Button,
  Stack,
  Grid,
  IconButton,
  Snackbar,
  Alert,
  TextField,
  Divider,
  Paper,
  Checkbox,
  FormControlLabel,
  Switch
} from "@mui/material";
import { Close } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";

const EditModal = ({ data, handleRefresh }) => {
  const AppService = useAppServices();
  const [openEditProduct, setOpenEditProduct] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [featuredList, setFeaturedList] = useState(false);
  const [city, setCity] = useState("");
  const [stateValue, setStateValue] = useState("");
  const [slug, setSlug] = useState("");
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const allAmenities = [
    "Parking Onsite",
    "Street Parking",
    "Toilets",
    "Secure Overnight Storage",
    "WIFI",
    "Wheelchair Friendly",
  ];

  const [images, setImages] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [houseRules, setHouseRules] = useState([]);
  const [newAmenity, setNewAmenity] = useState("");
  const [newRule, setNewRule] = useState("");
  const [parkingSpot, setParkingSpot] = useState("");
  const [streetSpot, setStreetSpot] = useState("");
  const [shootStyles, setShootStyles] = useState([]);
  const [newShootStyle, setNewShootStyle] = useState("");
  useEffect(() => {
    if (!data) return;

    if (data?.images) setImages(data.images);
    if (data?.amenities) setAmenities(data.amenities);
    if (data?.house_rules) setHouseRules(data.house_rules);
    if (data?.shootStyles) setShootStyles(data.shootStyles);
    if (data?.parkingSpot) setParkingSpot(data.parkingSpot);
    if (data?.slug) setSlug(data.slug);
    if (data?.address?.city) setCity(data?.address.city);
    if (data?.address?.state) setStateValue(data?.address.state);
    if (typeof data?.featuredList === "boolean") {
      setFeaturedList(data.featuredList);
    }
    else {
      const parking = data?.amenities?.find((a) => a.startsWith("Parking Onsite"));
      const match = parking?.match(/\((\d+)\s*spots?\)/);
      if (match) setParkingSpot(match[1]);
    }

    if (data?.streetSpot) setStreetSpot(data.streetSpot);
    else {
      const street = data?.amenities?.find((a) => a.startsWith("Street Parking"));
      const match = street?.match(/\((\d+)\s*spots?\)/);
      if (match) setStreetSpot(match[1]);
    }
  }, [data]);

  // Modal controls
  const handleModal = () => setOpenEditProduct(true);
  const closeEditProduct = () => setOpenEditProduct(false);

  // Upload image
  const handleUploadFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    for (const file of files) {
      if (!["image/jpeg", "image/png", "image/gif"].includes(file.type)) {
        setToast({
          open: true,
          message: `Invalid file type: ${file.name}`,
          severity: "error",
        });
        continue;
      }

      if (file.size > 10 * 1024 * 1024) {
        setToast({
          open: true,
          message: `File too large: ${file.name}`,
          severity: "error",
        });
        continue;
      }

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("desiredPath", "app/logo/image");

        const res = await uploadImageToGhl(formData);
        if (res?.data) {
          setImages((prev) => [...prev, res.data]);
          setToast({
            open: true,
            message: "File uploaded successfully!",
            severity: "success",
          });
        }
      } catch {
        setToast({
          open: true,
          message: "Upload failed!",
          severity: "error",
        });
      }
    }

    e.target.value = "";
  };

  const handleRemoveImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Add amenity / rule
  const handleAddAmenity = () => {
    if (newAmenity.trim()) {
      setAmenities((prev) => [...prev, newAmenity.trim()]);
      setNewAmenity("");
    }
  };



  const handleAddRule = () => {
    const trimmed = newRule.trim();
    if (!trimmed) return;
    setHouseRules((prev) => [...prev, { name: trimmed, checked: true }]);
    setNewRule("");

  };
  const handleRemoveRule = (ruleName) => {
    setHouseRules((prev) => prev?.filter((r) => r.name !== ruleName));
  };

  const handleAddShootStyle = () => {
    const trimmed = newShootStyle.trim();
    if (!trimmed) return;
    if (shootStyles.some((s) => s.toLowerCase() === trimmed.toLowerCase())) {
      setNewShootStyle("");
      return;
    }
    setShootStyles((prev) => [...prev, trimmed]);
    setNewShootStyle("");
  };
  const handleRemoveShootStyle = (styleName) => {
    setShootStyles((prev) => prev.filter((s) => s !== styleName));
  };

  // Save changes
  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      let updatedAmenities = amenities.filter(
        (a) => !a.startsWith("Parking Onsite") && !a.startsWith("Street Parking")
      );

      if (amenities.includes("Parking Onsite") || parkingSpot)
        updatedAmenities.push(`Parking Onsite (${parkingSpot || 0} spots)`);

      if (amenities.includes("Street Parking") || streetSpot)
        updatedAmenities.push(`Street Parking (${streetSpot || 0} spots)`);

      // Convert city/state to kebab-case for backend
      const formattedCity = city
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");

      const formattedState = stateValue
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");

      const payload = {
        _id: data?._id,
        images,
        slug,
        amenities: updatedAmenities,
        house_rules: houseRules,
        parkingSpot,
        streetSpot,
        featuredList,
        city: formattedCity,
        state: formattedState,
        shootStyles,
      };

      await AppService.listing.update({
        payload,
        toaster: true,
        message: "Listing updated successfully!",
      });

      handleRefresh?.();
      closeEditProduct();
    } catch (err) {
      console.error("Submit error:", err);
      setToast({
        open: true,
        message: "Error updating listing!",
        severity: "error",
      });
    } finally {
      setProcessing(false);
    }
  };


  // Modal style
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 700,
    maxHeight: "85vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 3,
  };
  const toggleRule = (index) => {
    setHouseRules((prev) =>
      prev.map((rule, i) =>
        i === index ? { ...rule, checked: !rule.checked } : rule
      )
    );
  };

  return (
    <>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
      <MDButton color="info" variant="contained" onClick={handleModal}>
        Edit Listing
      </MDButton>
      <MDModal open={openEditProduct} onClose={closeEditProduct}>
        <MDBox>
          <MDBox component="form" onSubmit={handleSubmit} sx={style}>
            <MDTypography variant="h5" mb={3} fontWeight="bold">
              Edit Listing
            </MDTypography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <MDTypography variant="button" fontWeight="bold">
                Featured Listing
              </MDTypography>
              <Divider sx={{ my: 1 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={featuredList}
                    onChange={(e) => setFeaturedList(e.target.checked)}
                    color="primary"
                  />
                }
                label={featuredList ? "Enabled" : "Disabled"}
              />
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <MDTypography variant="button" fontWeight="bold">
                Slug
              </MDTypography>
              <Divider sx={{ my: 1 }} />

              <TextField
                fullWidth
                size="small"
                placeholder="listing-slug"
                value={slug}
                onChange={(e) =>
                  setSlug(
                    e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]/g, "")
                      .replace(/\s+/g, "-")
                  )
                }
                helperText="URL-friendly identifier (lowercase, hyphens only)"
              />
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <MDTypography variant="button" fontWeight="bold">
                Location
              </MDTypography>
              <Divider sx={{ my: 1 }} />
              <Stack spacing={2}>
                <TextField
                  label="City"
                  size="small"
                  fullWidth
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  helperText="Enter city name"
                />
                <TextField
                  label="State"
                  size="small"
                  fullWidth
                  value={stateValue}
                  onChange={(e) => setStateValue(e.target.value)}
                  helperText="Enter state name"
                />
              </Stack>
            </Paper>





            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <MDTypography variant="button" fontWeight="bold">
                New Features
              </MDTypography>
              <Divider sx={{ my: 1 }} />

              {data?.newFeatureList?.length > 0 && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {data?.newFeatureList?.map((item, i) => (
                    <Grid item xs={12} sm={6} md={3} key={i}>
                      <MDTypography fontSize={16}>
                        {item?.name}
                      </MDTypography>
                    </Grid>

                  ))}
                </Grid>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <MDTypography variant="button" fontWeight="bold">
                New Styles
              </MDTypography>
              <Divider sx={{ my: 1 }} />
              {data?.newStyleList?.length > 0 && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {data?.newStyleList.map((item, i) => (
                    <Grid item xs={12} sm={6} md={3} key={i}>
                      <MDTypography fontSize={16}>
                        {item?.name}
                      </MDTypography>
                    </Grid>

                  ))}
                </Grid>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <MDTypography variant="button" fontWeight="bold">
                Shoot Styles
              </MDTypography>
              <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5 }}>
                Tag this home as good for certain shoot types (e.g. Fashion, Wedding) so it shows up on the matching location guide page.
              </MDTypography>
              <Divider sx={{ my: 1 }} />
              <MDBox sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {shootStyles.map((item) => (
                  <MDBox
                    key={item}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      bgcolor: "#EFEAFB",
                      borderRadius: 20,
                      pl: 1.5,
                      pr: 0.5,
                      py: 0.3,
                    }}
                  >
                    <MDTypography fontSize={13}>{item}</MDTypography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveShootStyle(item)}
                      sx={{ p: 0.3 }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </MDBox>
                ))}
              </MDBox>
              <Stack direction="row" spacing={1} mt={2}>
                <TextField
                  size="small"
                  placeholder="e.g. Fashion"
                  value={newShootStyle}
                  onChange={(e) => setNewShootStyle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddShootStyle();
                    }
                  }}
                  fullWidth
                />
                <Button
                  variant="contained"
                  onClick={handleAddShootStyle}
                  disabled={!newShootStyle.trim()}
                  sx={{ color: "#fff", minWidth: 120 }}
                >
                  Add
                </Button>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <MDTypography variant="button" fontWeight="bold">
                Amenities
              </MDTypography>
              <Divider sx={{ my: 1 }} />
              <Stack spacing={1}>
                {[...new Set([...allAmenities, ...(amenities?.map(a => a?.split(" (")[0]) || [])])]?.map(
                  (amenity, index) => {
                    const base = amenity.split(" (")[0];
                    const isChecked = amenities?.some(a => a.startsWith(base));
                    const existing = amenities?.find(a => a.startsWith(base));
                    const displayName =
                      (base === "Parking Onsite" && parkingSpot)
                        ? `${base} (${parkingSpot} spots)`
                        : (base === "Street Parking" && streetSpot)
                          ? `${base} (${streetSpot} spots)`
                          : existing || base;

                    return (
                      <Stack
                        key={index}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setAmenities(prev => [...prev, base]);
                                } else {
                                  setAmenities(prev => prev.filter(a => !a.startsWith(base)));
                                  if (base === "Parking Onsite") setParkingSpot("");
                                  if (base === "Street Parking") setStreetSpot("");
                                }
                              }}
                              sx={{ "&.Mui-checked": { color: "#7C6CCF" } }}
                            />
                          }
                          label={displayName}
                          sx={{ fontSize: 14 }}
                        />

                        {base === "Parking Onsite" && isChecked && (
                          <TextField
                            size="small"
                            type="number"
                            placeholder="Spots"
                            value={parkingSpot}
                            onChange={(e) => {
                              const val = e.target.value;
                              setParkingSpot(val);
                              setAmenities(prev => [
                                ...prev.filter(a => !a.startsWith("Parking Onsite")),
                                val ? `Parking Onsite (${val} spots)` : "Parking Onsite",
                              ]);
                            }}
                            sx={{ width: 100 }}
                          />
                        )}

                        {base === "Street Parking" && isChecked && (
                          <TextField
                            size="small"
                            type="number"
                            placeholder="Spots"
                            value={streetSpot}
                            onChange={(e) => {
                              const val = e.target.value;
                              setStreetSpot(val);
                              setAmenities(prev => [
                                ...prev.filter(a => !a.startsWith("Street Parking")),
                                val ? `Street Parking (${val} spots)` : "Street Parking",
                              ]);
                            }}
                            sx={{ width: 100 }}
                          />
                        )}
                      </Stack>
                    );
                  }
                )}
              </Stack>
              <Stack direction="row" spacing={1} mt={2}>
                <TextField
                  size="small"
                  placeholder="Add custom amenity"
                  value={newAmenity}
                  onChange={(e) => setNewAmenity(e.target.value)}
                  fullWidth
                />
                <Button
                  variant="contained"
                  onClick={handleAddAmenity}
                  disabled={!newAmenity.trim()}
                  sx={{ color: "#fff", minWidth: 120 }}
                >
                  Add
                </Button>
              </Stack>
            </Paper>


            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <MDTypography variant="button" fontWeight="bold">
                House Rules
              </MDTypography>
              <Divider sx={{ my: 1 }} />
              <Stack spacing={1}>
                {houseRules.map((rule, idx) => (
                  <Stack
                    key={idx}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rule.checked}
                          onChange={() => toggleRule(idx)}
                          sx={{
                            color: "#7C6CCF",
                            "&.Mui-checked": { color: "#7C6CCF" },
                          }}
                        />
                      }
                      label={rule.name}
                      sx={{ fontSize: 14 }}
                    />

                    {idx >= 6 && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveRule(rule.name)}
                        sx={{ color: "red" }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>

              <Stack direction="row" spacing={1} mt={2}>
                <TextField
                  size="small"
                  placeholder="Add custom rule"
                  value={newRule}
                  onChange={(e) => setNewRule(e.target.value)}
                  fullWidth
                />
                <Button
                  variant="contained"
                  onClick={handleAddRule}
                  disabled={!newRule.trim()}
                  sx={{ color: "#fff", minWidth: 120 }}
                >
                  Add
                </Button>
              </Stack>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <MDTypography variant="button" fontWeight="bold">
                Images
              </MDTypography>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" spacing={2} mb={2}>
                <Button variant="contained" component="label" sx={{ color: "#fff", width: 150 }}>
                  Upload +
                  <input hidden type="file" multiple onChange={handleUploadFile} />
                </Button>
              </Stack>
              <Grid container spacing={1}>
                {images.map((img, index) => (
                  <Grid item xs={3} sm={2} md={2} key={index}>
                    <div
                      style={{
                        position: "relative",
                        borderRadius: 6,
                        overflow: "hidden",
                        border: "1px solid #ddd",
                      }}
                    >
                      <img
                        src={img}
                        alt={`preview-${index}`}
                        style={{
                          width: "100%",
                          height: 80,
                          objectFit: "cover",
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveImage(index)}
                        sx={{
                          position: "absolute",
                          top: 2,
                          right: 2,
                          backgroundColor: "rgba(255,255,255,0.8)",
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </div>
                  </Grid>
                ))}
              </Grid>
            </Paper>
            <MDBox display="flex" justifyContent="flex-end" mt={2}>
              <MDButton variant="gradient" color="primary" onClick={closeEditProduct}>
                Close
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                type="submit"
                sx={{ ml: 2 }}
                disabled={processing}
              >
                Save
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDModal>
    </>
  );
};

export default EditModal;
